/**
 * Kanonisches Format der Sperrschlüssel `RawRecordInput.blockKeys` (B-C1, V12-18; K-4 aus Plan Welle 1 b).
 *
 * Eine Funktion für Konnektor **und** Kern: Konnektor und Löschvorbereitung (`erasure_block`, `raw_record_key`)
 * müssen vor dem Hash (HMAC mit `ERASURE_PEPPER`) identische Klartexte bilden. Es gibt **keinen** Rückfall auf
 * Rohwerte (rohe URL oder URN) und keine Namen: die Namensschwärzung bildet der Kern selbst (`name:<nameKey>`).
 *
 * - `identity:<namespace>:profile_url:<normalisierte Profil-URL>`
 * - `identity:<namespace>:member_urn:<URN>`
 * - `identity:email:email:<Adresse in Kleinbuchstaben>` (Microsoft 365, Odoo; Plan Welle 2 A, VB-2 a)
 * - `identity:odoo:odoo_partner:<Ursprung>#<Nummer>` (Odoo-Partner, z. B. `https://erp.beispiel.example#42`)
 * - `conversation:<connectorId>:<externalId>` (LinkedIn DMA: `connectorId = linkedin-dma`)
 *
 * Das Präfix entspricht `erasure_block.block_kind` (`identity`, `conversation`). Fehlermeldungen enthalten nie den
 * übergebenen Wert (keine Klartexte in Protokollen).
 */
import { z } from 'zod';
import { ConnectorError } from './errors.js';
import { type IdentityRef, IdentityRefSchema } from './refs.js';

/** Obergrenze der Schlüssel je Datensatz (vorher 64, V12-18). */
export const BLOCK_KEYS_MAX = 256;
const BLOCK_KEY_MAX_LENGTH = 512;
const CONNECTOR_ID_PATTERN = /^[a-z][a-z0-9-]{0,63}$/;
const EXTERNAL_ID_PATTERN = /^\S{1,200}$/;

/** Quelle eines Sperrschlüssels: Identität (Profil-URL, URN, E-Mail-Adresse, Odoo-Partner) oder Gespräch. */
export type BlockKeySource =
  | { kind: 'identity'; identity: IdentityRef }
  | { kind: 'conversation'; connectorId: string; externalId: string };

/** Bildet den kanonischen Schlüssel; wirft `RangeError` (ohne den Wert zu nennen) bei ungültiger Eingabe. */
export function canonicalBlockKey(source: BlockKeySource): string {
  if (source.kind === 'identity') {
    if (!IdentityRefSchema.safeParse(source.identity).success) {
      throw new RangeError('canonicalBlockKey: Identität passt nicht zum Muster ihrer Art');
    }
    const { namespace, kind, value } = source.identity;
    return `identity:${namespace}:${kind}:${value}`;
  }
  if (!CONNECTOR_ID_PATTERN.test(source.connectorId)) {
    throw new RangeError('canonicalBlockKey: ungültige connectorId');
  }
  if (!EXTERNAL_ID_PATTERN.test(source.externalId)) {
    throw new RangeError('canonicalBlockKey: ungültige externalId');
  }
  return `conversation:${source.connectorId}:${source.externalId}`;
}

const IDENTITY_KEY = /^identity:([a-z]+):(profile_url|member_urn|email|odoo_partner):(.+)$/;
const CONVERSATION_KEY = /^conversation:([^:]+):(.+)$/;

/** Prüft zur Laufzeit, ob ein Wert ein kanonischer Sperrschlüssel ist (Umkehrung von `canonicalBlockKey`). */
export function isCanonicalBlockKey(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > BLOCK_KEY_MAX_LENGTH) {
    return false;
  }
  const identity = IDENTITY_KEY.exec(value);
  if (identity !== null) {
    return IdentityRefSchema.safeParse({
      namespace: identity[1],
      kind: identity[2],
      value: identity[3],
    }).success;
  }
  const conversation = CONVERSATION_KEY.exec(value);
  return (
    conversation !== null &&
    CONNECTOR_ID_PATTERN.test(conversation[1] ?? '') &&
    EXTERNAL_ID_PATTERN.test(conversation[2] ?? '')
  );
}

/** Element von `RawRecordInput.blockKeys`: nur kanonische Schlüssel. */
export const BlockKeySchema = z.string().refine(isCanonicalBlockKey, {
  message: 'kein kanonischer Sperrschlüssel (identity:… oder conversation:…)',
});

/**
 * Kanonische, lexikografisch sortierte Liste ohne Dubletten. Mehr als `BLOCK_KEYS_MAX` verschiedene Schlüssel
 * → `ConnectorError('protocol', 'unknown_shape')` (K-4, RB-11).
 */
export function canonicalBlockKeys(sources: readonly BlockKeySource[]): string[] {
  const keys = [...new Set(sources.map(canonicalBlockKey))].sort();
  if (keys.length > BLOCK_KEYS_MAX) {
    throw new ConnectorError('protocol', 'unknown_shape');
  }
  return keys;
}
